import {
  ConnectedPosition,
  Overlay,
  OverlayRef,
  ScrollStrategyOptions,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, Injector } from '@angular/core';
import type { MenuItemInput } from '@app/models/document.model';
import { DropdownMenuComponent } from './dropdown-menu.component';

type Point = { x: number; y: number };

@Injectable({ providedIn: 'root' })
export class DropdownOverlayService {
  private overlayRef?: OverlayRef;
  constructor(
    private overlay: Overlay,
    private sso: ScrollStrategyOptions,
    private injector: Injector
  ) {}

  openAtPoint(
    point: Point,
    items: ReadonlyArray<MenuItemInput>,
    onPick: (key: string) => void
  ): void {
    this.close();

    const GUTTER_X = 30,
      DOWN_Y = 8,
      UP_Y = 32;

    const positions: ConnectedPosition[] = [
      {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'top',
        offsetX: -GUTTER_X,
        offsetY: DOWN_Y,
      }, // bottom-left
      {
        originX: 'start',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'top',
        offsetX: GUTTER_X,
        offsetY: DOWN_Y,
      }, // bottom-right
      {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
        offsetX: -GUTTER_X,
        offsetY: -UP_Y,
      }, // top-left
      {
        originX: 'start',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'bottom',
        offsetX: GUTTER_X,
        offsetY: -UP_Y,
      }, // top-right
    ];

    const strategy = this.overlay
      .position()
      .flexibleConnectedTo(point)
      .withPositions(positions)
      .withFlexibleDimensions(true)
      .withViewportMargin(8)
      .withPush(true);

    this.overlayRef = this.overlay.create({
      positionStrategy: strategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this.sso.reposition(),
      panelClass: 'pc-overlay-panel',
    });

    const portal = new ComponentPortal(
      DropdownMenuComponent,
      null,
      this.injector
    );
    const compRef = this.overlayRef.attach(portal);
    compRef.setInput('items', items);

    const paneEl = this.overlayRef.overlayElement;
    const panelEl = paneEl.querySelector('.pc-overlay') as HTMLElement;

    const clamp = (value: number, min: number, max: number) =>
      Math.max(min, Math.min(max, value));
    const applyPlacement = () => {
      const rect = paneEl.getBoundingClientRect();
      const left = clamp(point.x - rect.left, 10, rect.width - 10);
      panelEl.style.setProperty('--pc-arrow-left', `${Math.round(left)}px`);
      const isAbove = rect.bottom <= point.y;
      panelEl.setAttribute('data-placement', isAbove ? 'top' : 'bottom');
    };

    setTimeout(applyPlacement, 0);
    strategy.positionChanges.subscribe(() => applyPlacement());

    const sub = compRef.instance.pick.subscribe((key) => {
      onPick(key);
      this.close();
    });
    this.overlayRef.backdropClick().subscribe(() => this.close());
    this.overlayRef.detachments().subscribe(() => sub.unsubscribe());
  }

  close(): void {
    this.overlayRef?.dispose();
    this.overlayRef = undefined;
  }
}
